import { FormComponentFactory } from "../FormComponentFactory";
import { ComponentConfig, Component } from "../Component";
import {
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import {
  ColumnSelector,
  allColumnsForDataset,
} from "datasheets/components/selectors/ColumnSelector";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import { atom, useAtomValue } from "jotai";
import { datasetsAtom } from "datasheets/model/atoms/WorkbookFocusAtoms";
import {
  TabularConfigAtomType,
  TabularDataConfig,
  TabularDataView,
} from "datasheets/components/TabularDataView";
import {
  componentCustomOrEditConfigAtom,
  useComponentConfigAtom,
  wireStoreAtom,
} from "../state/ComponentAtoms";
import { Add, Cancel } from "@mui/icons-material";
import { ColumnFilter, Operator } from "datasheets/components/RowFilter";
import { dictionaryValueMap } from "datasheets/model/utils/dictionaryUtils";

export interface DataTableComponent
  extends ComponentConfig,
    TabularDataConfig {}
// const ValidityRequirements = {
//   datasetId: true,
// };
const ComponentName = "Table";
export function registerDataTableComponent() {
  FormComponentFactory.addComponent(ComponentName, {
    config: (): DataTableComponent => {
      return {
        columnMapping: {},
        columnDisplayProperties: {},
        connections: {
          inputs: [],
          outputs: [],
          inputConnectors: {},
          outputConnectors: {},
        },
      };
    },
    component: (config: Component) => {
      return <DataTable componentId={config.id} />;
    },
    editor: (config: Component) => {
      return <DataTableEditor componentId={config.id} />;
    },
  });
  // Add old names for legacy config
  FormComponentFactory.addAlias("DataTable", ComponentName);
}

function DataTable({ componentId }: { componentId: string }) {
  const configAtom = useMemo(
    () => componentCustomOrEditConfigAtom(componentId),
    [componentId]
  );
  const dataTableConfigAtom: TabularConfigAtomType = useMemo(() => {
    return atom<
      TabularDataConfig,
      TabularDataConfig | ((prev: TabularDataConfig) => TabularDataConfig)
    >(
      (get) => get(configAtom) as DataTableComponent,
      (get, set, arg) => set(configAtom, arg)
    );
  }, [configAtom]);
  const wireStore = useAtomValue(wireStoreAtom);
  const config = useAtomValue(configAtom);
  const variables = dictionaryValueMap(
    config.connections?.inputConnectors ?? [],
    (wireId: string) => wireStore[wireId]
  );
  const width = 520;
  const height = 200;
  const children = (
    <TabularDataView
      width={width}
      height={height}
      tabularConfigAtom={dataTableConfigAtom}
      fixedColumns={true}
      variables={variables}
    />
  );
  return <Card>{children}</Card>;
}

const DataTableEditor = memo(({ componentId }: { componentId: string }) => {
  const datasets = useAtomValue(datasetsAtom);
  const [config, setConfig] = useComponentConfigAtom(componentId);
  const componentConfig = config.editConfig as DataTableComponent;
  const datasetId = componentConfig.datasetId;
  function setDatasetId(datasetId?: string): void {
    // TODO: configure columns. For the moment just set them all
    let columnMapping = {};
    if (!!datasetId && datasetId in datasets) {
      const dataset = datasets[datasetId];
      columnMapping = allColumnsForDataset(dataset.properties);
    }
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: {
            ...componentConfig,
            ...{ datasetId: datasetId, columnMapping: columnMapping },
          },
        },
      };
    });
  }
  const onChangeColumns = useCallback(
    (columns: { [index: number]: string }): void => {
      setConfig((config: Component) => {
        return {
          ...config,
          ...{
            editConfig: {
              ...config.editConfig,
              ...{
                datasetId: (config.editConfig as DataTableComponent).datasetId,
                columnMapping: columns,
              },
            },
          },
        };
      });
    },
    [setConfig]
  );
  const onChangeFilter = useCallback(
    (filters: ColumnFilter[]): void => {
      const rowFilter =
        !filters || filters.length <= 0
          ? undefined
          : { columnFilters: filters };
      setConfig((config: Component) => {
        return {
          ...config,
          ...{
            editConfig: {
              ...config.editConfig,
              ...{
                rowFilter: rowFilter,
              },
            },
          },
        };
      });
    },
    [setConfig]
  );
  return (
    <>
      <div style={{ fontSize: ".5em" }}>{config.id}</div>
      <DataSetSelector onSelect={setDatasetId} selection={datasetId} />
      <ColumnSelector
        title="Select Columns"
        datasetId={datasetId}
        selection={Object.values(componentConfig.columnMapping)}
        onChange={onChangeColumns}
        isSingleSelection={false}
      />
      <FilterSelector
        datasetId={datasetId}
        componentConfig={componentConfig}
        onChange={onChangeFilter}
      />
    </>
  );
});

function FilterSelector({
  datasetId,
  componentConfig,
  onChange,
}: {
  datasetId: string | undefined;
  componentConfig: DataTableComponent;
  onChange: (filters: ColumnFilter[]) => void;
}) {
  const [showAddColumnSelector, setShowAddColumnSelector] = useState(false);
  const anchorEl = useRef(null);
  const handleMenuOpen = (event: any) => {
    setShowAddColumnSelector(true);
  };

  const handleMenuClose = () => {
    setShowAddColumnSelector(false);
  };

  const datasets = useAtomValue(datasetsAtom);
  if (!datasetId) {
    return <></>;
  }
  const dataset = datasets[datasetId];
  const filters = componentConfig.rowFilter?.columnFilters ?? [];
  const allColumns = allColumnsForDataset(dataset.properties);
  const addColumnOptions = Object.values(allColumns).map((columnKey) => {
    return (
      <MenuItem
        key={columnKey}
        onClick={() => {
          addFilterForColumn(columnKey);
          handleMenuClose();
        }}
      >
        <ListItemText>{dataset.properties.keyToName[columnKey]}</ListItemText>
      </MenuItem>
    );
  });

  const removeFilterAt = (index: number) => {
    delete filters[index];
    onChange(filters);
  };
  const addFilterForColumn = (columnKey: string) => {
    filters[filters.length] = {
      columnKey: columnKey,
      operator: "EQ",
      operand: "",
    };
    onChange(filters);
  };
  const updateFilterAt = (index: number, newFilter: ColumnFilter) => {
    filters[index] = newFilter;
    onChange(filters);
  };
  return (
    <List>
      Add Filters
      <Add onClick={handleMenuOpen} ref={anchorEl} />
      {filters.map((columnFilter, index) => {
        return (
          <ListItem key={columnFilter.columnKey + index}>
            <FilterEditor
              columnFilter={columnFilter}
              columnName={dataset.properties.keyToName[columnFilter.columnKey]}
              key={columnFilter.columnKey + index}
              onChange={(filter: ColumnFilter) => updateFilterAt(index, filter)}
            />
            <ListItemIcon onClick={() => removeFilterAt(Number(index))}>
              <Cancel />
            </ListItemIcon>
          </ListItem>
        );
      })}
      <Menu
        open={showAddColumnSelector}
        onClose={() => {
          handleMenuClose();
        }}
        anchorEl={anchorEl.current}
      >
        {addColumnOptions}
      </Menu>
    </List>
  );
}

function FilterEditor({
  columnFilter,
  columnName,
  onChange,
}: {
  columnFilter: ColumnFilter;
  columnName: string;
  onChange: (columnFilter: ColumnFilter) => void;
}) {
  return (
    <Stack direction="column">
      <ListItemText>{columnName}</ListItemText>
      <Select
        size="small"
        value={columnFilter.operator}
        onChange={(event) =>
          onChange({
            ...columnFilter,
            ...{ operator: event.target.value as Operator },
          })
        }
      >
        {["EQ", "GT", "GE", "LT", "LE"].map((op) => {
          return <MenuItem value={op}>{op}</MenuItem>;
        })}
      </Select>
      <TextField
        hiddenLabel
        size="small"
        defaultValue={columnFilter.operand}
        onChange={(event) =>
          onChange({ ...columnFilter, ...{ operand: event.target.value } })
        }
      />
    </Stack>
  );
}
